import {
  agencyPrimaryAction, deriveInvoiceState, dunningStageLabel, holidayDates, invoiceFromRow,
  type InvoiceRecord, type InvoiceRow as DomainInvoiceRow, type InvoiceStateContext, type InvoiceStateResult, type OrgConfig, type PrimaryAction,
} from "@talentkontor/domain";
import type { InvoiceRow, PeriodRow } from "../data/queries";
import { t, currentLang } from "../lib/sprache";

/** Rechnungen für die Anzeige: Fachlogik-Zustand, Statuspille mit Zeichen, nächster Schritt. */

export type Tone = "neutral" | "info" | "warning" | "success" | "error";
export interface Pill {
  label: string;
  tone: Tone;
  symbol: string;
}
const SYMBOL: Record<Tone, string> = { neutral: "○", info: "◇", warning: "◷", success: "✓", error: "!" };
const pill = (label: string, tone: Tone): Pill => ({ label, tone, symbol: SYMBOL[tone] });

export interface ViewContext {
  cfg: OrgConfig;
  today: string;
  /** Feiertagsregion der Agentur (org_settings.holiday_region), z. B. DE-HH */
  holidayRegion: string | null;
  periods?: ReadonlyMap<string, PeriodRow>;
  /** Fälligkeit der Auszahlung je Creator-Rechnung an die Agentur (invoice_payout_info, 0055); null = Marke hat noch nicht gezahlt */
  payoutDue?: ReadonlyMap<string, string | null>;
}

/**
 * Feiertagsregion für Fristen und Werktage: die gewählte Region, ohne Wahl die bundesweiten Feiertage des Landes der Agentur
 * (SAAS-R3-11, wie im Creator-Portal und im Katalog; vorher rechneten die Agenturansichten dann ohne Feiertage).
 */
export function holidayRegionOf(settings: Readonly<Record<string, unknown>> | null | undefined): string | null {
  const region = settings?.["holiday_region"];
  const country = settings?.["country"];
  const r = typeof region === "string" ? region.trim() : "";
  if (r) return r;
  const c = typeof country === "string" ? country.trim().toUpperCase() : "";
  return c || null;
}

const holidayCache = new Map<string, string[]>();
function holidays(region: string | null, today: string): string[] {
  const key = `${region ?? ""}:${today.slice(0, 4)}`;
  let h = holidayCache.get(key);
  if (!h) {
    const year = Number(today.slice(0, 4));
    try {
      h = region ? holidayDates(region, [year - 1, year, year + 1]) : [];
    } catch {
      h = [];
    }
    holidayCache.set(key, h);
  }
  return h;
}

export function stateContext(v: ViewContext, opts: { brandPaidForPeriod?: boolean } = {}): InvoiceStateContext {
  return {
    today: v.today,
    policy: v.cfg.dunning,
    settings: v.cfg.settings,
    holidays: holidays(v.holidayRegion, v.today),
    ...(opts.brandPaidForPeriod !== undefined ? { brandPaidForPeriod: opts.brandPaidForPeriod } : {}),
  };
}

export interface InvoiceView {
  row: InvoiceRow;
  record: InvoiceRecord;
  state: InvoiceStateResult;
  pill: Pill;
  next: PrimaryAction | null;
}

export function viewInvoice(row: InvoiceRow, v: ViewContext): InvoiceView | null {
  let record: InvoiceRecord;
  try {
    record = invoiceFromRow(row as unknown as DomainInvoiceRow, v.cfg.settings.timeZone);
  } catch {
    return null;
  }
  if (row.kind === "talent_to_agency" && v.payoutDue?.has(row.id)) record = { ...record, payoutDueDate: v.payoutDue.get(row.id) ?? null };
  const period = row.period_id ? v.periods?.get(row.period_id) : undefined;
  const ctx = stateContext(v, period ? { brandPaidForPeriod: period.status === "paid" } : {});
  const state = deriveInvoiceState(record, ctx);
  return { row, record, state, pill: statePill(state, v.cfg, row.kind, !!row.self_billed), next: agencyPrimaryAction(record, ctx) };
}

export function statePill(s: InvoiceStateResult, cfg: OrgConfig, kind?: string, selfBilled = false): Pill {
  switch (s.state) {
    case "draft": return pill(t("Entwurf", "Draft"), "neutral");
    case "in_review": return pill(t("In Prüfung", "In review"), "warning");
    case "approved": return pill(t("Freigegeben", "Approved"), "info");
    case "send_overdue": return pill(t("Versand überfällig", "Sending overdue"), "error");
    case "delivery_pending": return pill(t("Versand läuft", "Sending"), "info");
    case "delivery_failed": return pill(t("Versand gescheitert", "Sending failed"), "error");
    case "payout_overdue": return pill(t("Auszahlung überfällig", "Payout overdue"), "error");
    case "payout_confirmed": return pill(t("Ausgezahlt · Bestätigung offen", "Paid out · confirmation open"), "info");
    case "sent": return pill(kind === "talent_to_agency" ? t("Zur Auszahlung vorgemerkt", "Marked for payout") : t("Offen", "Open"), "info");
    case "due_soon": return pill(t("Bald fällig", "Due soon"), "warning");
    case "due_today": return pill(t("Heute fällig", "Due today"), "warning");
    case "overdue": return pill(t("Überfällig", "Overdue"), "error");
    case "dunning": {
      // Stufe wie in Mahnleiter und Mahnseite (dunningStageLabel): Erinnerungen zählen nicht mit (1. Mahnung, 2. Mahnung …)
      const stage = cfg.dunning.stages.find((x) => x.key === s.stageKey);
      // K-404: Erinnerung gelb (warning), jede Mahnung rot (error), wie die Überschrift des Schreibens
      return pill(stage?.isReminder ? t("Erinnert", "Reminded") : dunningStageLabel(cfg.dunning, s.stageKey, currentLang()), stage?.isReminder ? "warning" : "error");
    }
    case "payment_reported": return pill(t("Zahlung gemeldet", "Payment reported"), "info");
    case "money_missing": return pill(t("Zahlung fehlt", "Payment missing"), "error");
    case "correction_needed": return pill(t("Korrektur nötig", "Correction needed"), "warning");
    case "collection": return pill(t("Inkasso", "Debt collection"), "error");
    case "paid": return pill(kind === "talent_to_agency" ? t("Ausgezahlt", "Paid out") : t("Bezahlt", "Paid"), "success");
    case "bad_debt": return pill(t("Forderungsausfall", "Bad debt"), "neutral");
    case "cancelled": return pill(t("Storniert", "Cancelled"), "neutral");
    case "cancelled_refund_pending": return kind === "talent_to_agency"
      ? pill(t("Storniert · Rückforderung offen", "Cancelled · repayment claim open"), "warning")
      : pill(t("Storniert · Erstattung offen", "Cancelled · refund open"), "warning");
    case "credit_note": return pill(selfBilled ? t("Stornobeleg", "Cancellation document") : t("Stornorechnung", "Cancellation invoice"), "neutral");
    case "withdrawn": return pill(t("Zurückgezogen", "Withdrawn"), "neutral");
    case "discarded": return pill(t("Verworfen", "Discarded"), "neutral");
  }
}

/** Beschriftung des nächsten Schritts (Aktionen aus 15_texte.md). */
export function actionLabel(a: PrimaryAction | null, kind?: string): string {
  if (!a) return t("Details ansehen", "View details");
  const key = a.labelKey.replace(/^action\.|^hint\./, "");
  // Rechnung des Creators an die eigene Agentur (WS-17): kein Versand an eine Marke, sondern Zahlungslauf
  if (kind === "talent_to_agency" && key === "send_to_brand") return t("Zur Auszahlung vormerken", "Mark for payout");
  if (kind === "agency_to_talent" && (key === "send_to_brand" || key === "resend_to_brand")) return t("An den Creator senden", "Send to the creator");
  // Rechnung des Creators, storniert und schon ausgezahlt: Rückforderung, keine Erstattung (Nachtest K-157)
  if (kind === "talent_to_agency" && key === "check_refund") return t("Rückforderung prüfen", "Check repayment claim");
  const labels: Record<string, string> = {
    review: t("Rechnung prüfen", "Review invoice"),
    approve: t("Rechnung freigeben", "Approve invoice"),
    send_to_brand: t("An Marke senden", "Send to brand"),
    resend_to_brand: t("Erneut senden", "Send again"),
    send_dunning: t("Zahlung erinnern", "Send payment reminder"),
    confirm_payment: t("Zahlung bestätigen", "Confirm payment"),
    confirm_payout_and_ask: t("Überweisung vermerken", "Note transfer"),
    request_payment_check: t("Zahlung nachfragen", "Ask about payment"),
    check_payment: t("Zahlung prüfen", "Check payment"),
    check_refund: t("Erstattung prüfen", "Check refund"),
    hand_over_to_collection: t("Inkasso prüfen", "Check debt collection"),
    extend_due_date: t("Frist verlängern", "Extend deadline"),
    waiting_for_talent: t("Wartet auf Creator", "Waiting for creator"),
    waiting_for_brand: t("Wartet auf Zahlung der Marke", "Waiting for brand payment"),
    delivery_pending: t("Versand läuft", "Sending"),
    // GELD-R3-1 und CREATOR-R3-6 (0091)
    payout_rest: t("Rest auszahlen (Auszahlungslauf)", "Pay out the rest (payout run)"),
    record_transfer: t("Zahlungseingang erfassen", "Record incoming payment"),
  };
  return labels[key] ?? t("Details ansehen", "View details");
}

export const RESPONSIBLE: Record<string, string> = { get agency() { return t("Agentur", "Agency"); }, talent: "Creator", get brand() { return t("Marke", "Brand"); }, none: "–" };
